import ConditionTypeEnum from '~Api/Application/ConditionTypeEnum';
import INote from '~Api/Application/INote';
import IApplicationWarehouse from '~Api/Application/IApplicationWarehouse';
import ApplicationFeeFormatEnum from '~Api/Deal/ApplicationFeeFormatEnum';
import BrokerageFeeFormatEnum from '~Api/Deal/BrokerageFeeFormatEnum';
import EstablishmentFeeFormatEnum from '~Api/Deal/EstablishmentFeeFormatEnum';
import IDealBorrower from '~Api/Deal/IDealBorrower';
import { transformDealBorrower } from '~Api/Deal/transformers';
import { fetchDelete, fetchGet, fetchPost, fetchPut } from '~utilities/fetch';
import CloseReasonEnum from './CloseReasonEnum';
import IApplication from './IApplication';
import IApplicationDisbursement from './IApplicationDisbursement';
import IApplicationFee from './IApplicationFee';
import IBorrower from './IBorrower';
import IConditionalCondition from './IConditionalCondition';
import {
    transformApplication,
    transformApplicationBorrower,
    transformApplicationConditionDocument,
    transformApplicationConditionalCondition,
    transformApplicationDisbursement,
    transformApplicationFee,
    transformApplicationNote,
    transformApplicationWarehouse,
} from './transformers';
import IConditionDocument from './IConditionDocument';
import ListSortFieldEnum from './ListSortFieldEnum';
import ListSortOrderEnum from './ListSortOrderEnum';
import PropertyStateEnum from '~Api/Deal/PropertyStateEnum';
import PropertyStreetTypeEnum from '~Api/Deal/PropertyStreetTypeEnum';
import WorkflowStatusEnum from './WorkflowStatusEnum';
import InterestMethodEnum from './InterestMethodEnum';
import PropertyValuerValuationTypeEnum from './PropertyValuerValuationType';
import PropertyValuationTypeEnum from './PropertyValuationType';

export function applicationsCreateRenewalFromLoanRequest(
    applicationFee: number,
    applicationFeeFormat: ApplicationFeeFormatEnum,
    applicationFeePercentage: number,
    brokerageFee: number,
    brokerageFeeFormat: BrokerageFeeFormatEnum,
    brokerageFeePercentage: number,
    establishmentFee: number,
    establishmentFeeFormat: EstablishmentFeeFormatEnum,
    establishmentFeePercentage: number,
    estimatedOutlays: number,
    interestMethod: InterestMethodEnum,
    interestRate: number,
    legalFees: number,
    loanAmount: number,
    loanPayoutFigureUuid: string,
    lvr: number,
    maximumLvr: number,
    netBalance: number,
    termMonths: number,
): Promise<Response> {
    return fetchPost('/applications/create-renewal-from-loan', {
        applicationFee,
        applicationFeeFormat,
        applicationFeePercentage,
        brokerageFee,
        brokerageFeeFormat,
        brokerageFeePercentage,
        establishmentFee,
        establishmentFeeFormat,
        establishmentFeePercentage,
        estimatedOutlays,
        interestMethod,
        interestRate,
        legalFees,
        loanAmount,
        loanPayoutFigureUuid,
        lvr,
        maximumLvr,
        netBalance,
        termMonths,
    });
}

export async function applicationsDashboardRequest(): Promise<Response> {
    return fetchGet('/applications/dashboard');
}

export async function applicationAssignRequest(applicationUuid: string, administratorUuid: string): Promise<Response> {
    return fetchPost(`/applications/${applicationUuid}/assign`, {
        administratorUuid,
    });
}

export async function applicationsExpiringValuationsListRequest(): Promise<Response> {
    return fetchGet('/applications/expiring-valuations');
}

export async function applicationsPaginatedListRequest(
    closed: boolean,
    loanOfficerUuids: string[],
    loanProcessorUuids: string[],
    order: ListSortOrderEnum,
    orderBy: ListSortFieldEnum,
    page: number,
    perPage: number,
    statuses: WorkflowStatusEnum[],
): Promise<Response> {
    return fetchGet('/applications', {
        closed,
        loanOfficerUuids,
        loanProcessorUuids,
        order,
        orderBy,
        page,
        perPage,
        statuses,
    });
}

export async function applicationsSettlementForecastRequest(): Promise<Response> {
    return fetchGet('/applications/settlement-forecast');
}

export async function applicationsSettlementReportRequest(startDate: string, endDate: string): Promise<Response> {
    return fetchGet('/applications/settlement-report', {
        endDate,
        startDate,
    });
}

export async function applicationsSearchRequest(keyword: string): Promise<Response> {
    return fetchPost('/applications/search', { keyword });
}

export async function applicationsSettlementProbabilitiesRequest(): Promise<Response> {
    return fetchGet('/applications/settlement-probabilities', { version: 'v2' });
}

export async function applicationBorrowersListRequest(applicationUuid: string): Promise<Response> {
    return fetchGet(`/applications/${applicationUuid}/borrowers`);
}

export async function applicationBorrowersAddRequest(applicationUuid: string, dealBorrower: IDealBorrower, applicationBorrower: IBorrower): Promise<Response> {
    return fetchPost(`/applications/${applicationUuid}/borrowers`, {
        ...transformDealBorrower(dealBorrower),
        ...transformApplicationBorrower(applicationBorrower),
    });
}

export async function applicationBorrowersDeleteRequest(borrowerUuid: string): Promise<Response> {
    return fetchDelete(`/application-borrowers/${borrowerUuid}`);
}

export async function applicationBorrowersUpdateRequest(applicationBorrower: IBorrower): Promise<Response> {
    return fetchPut(`/application-borrowers/${applicationBorrower.uuid}`, {
        ...transformDealBorrower(applicationBorrower.dealBorrower),
        ...transformApplicationBorrower(applicationBorrower),
    });
}

export async function applicationBorrowerBankruptcyCheckRequest(borrowerUuid: string, useMiddleName: boolean): Promise<Response> {
    return fetchPost(`/application-borrowers/${borrowerUuid}/bankruptcy-check`, {
        useMiddleName,
    });
}

export async function applicationBorrowerCompanyTradingHistoryCheckRequest(borrowerUuid: string): Promise<Response> {
    return fetchPost(`/application-borrowers/${borrowerUuid}/company-trading-history-check`);
}

export async function applicationBorrowerCreditCheckRequest(borrowerUuid: string): Promise<Response> {
    return fetchPost(`/application-borrowers/${borrowerUuid}/credit-check`);
}

export async function applicationAssignPayoutFigureRequest(applicationUuid: string, loanPayoutFigureUuid: string): Promise<Response> {
    return fetchPost(`/applications/${applicationUuid}/assign-payout-figure`, { loanPayoutFigureUuid });
}

export async function applicationCloseRequest(applicationUuid: string, reason: CloseReasonEnum, note: string): Promise<Response> {
    return fetchPost(`/applications/${applicationUuid}/close`, {
        note,
        reason,
    });
}

export async function applicationConditionDeleteRequest(conditionUuid: string): Promise<Response> {
    return fetchDelete(`/application-conditions/${conditionUuid}`);
}

export async function applicationConditionRequiredWorkflowStatusRequest(
    conditionUuid: string,
    requiredWorkflowStatus: WorkflowStatusEnum,
): Promise<Response> {
    return fetchPost(`/application-conditions/${conditionUuid}/required-workflow-status`, {
        requiredWorkflowStatus,
    });
}

export async function applicationConditionMarkSatisfiedRequest(applicationConditionUuid: string): Promise<Response> {
    return fetchPost(`/application-conditions/${applicationConditionUuid}/mark-satisfied`);
}

export async function applicationConditionMarkUnsatisfiedRequest(applicationConditionUuid: string): Promise<Response> {
    return fetchPost(`/application-conditions/${applicationConditionUuid}/mark-unsatisfied`);
}

export async function applicationConditionsAddRequest(
    applicationUuid: string,
    name: string,
    requiredWorkflowStatus: WorkflowStatusEnum,
): Promise<Response> {
    return fetchPost(`/applications/${applicationUuid}/conditions`, {
        name,
        requiredWorkflowStatus,
    });
}

export async function applicationBorrowerConditionsAddRequest(
    applicationBorrowerUuid: string,
    name: string,
    requiredWorkflowStatus: WorkflowStatusEnum,
): Promise<Response> {
    return fetchPost(`/application-borrowers/${applicationBorrowerUuid}/conditions`, {
        name,
        requiredWorkflowStatus,
    });
}

export async function applicationPropertyConditionsAddRequest(
    applicationPropertyUuid: string,
    name: string,
    requiredWorkflowStatus: WorkflowStatusEnum,
): Promise<Response> {
    return fetchPost(`/application-properties/${applicationPropertyUuid}/conditions`, {
        name,
        requiredWorkflowStatus,
    });
}

export async function applicationConditionsListRequest(applicationUuid: string): Promise<Response> {
    return fetchGet(`/applications/${applicationUuid}/conditions`);
}

export async function applicationConditionDocumentsAddRequest(applicationUuid: string, conditionDocument: IConditionDocument): Promise<Response> {
    return fetchPost(`/applications/${applicationUuid}/condition-documents`, transformApplicationConditionDocument(conditionDocument));
}

export async function applicationConditionDocumentApproveRequest(conditionDocumentUuid: string): Promise<Response> {
    return fetchPost(`/application-condition-documents/${conditionDocumentUuid}/approve`);
}

export async function applicationConditionDocumentDeleteRequest(conditionDocumentUuid: string): Promise<Response> {
    return fetchDelete(`/application-condition-documents/${conditionDocumentUuid}`);
}

export async function applicationConditionDocumentsSolicitorAccessUpdateRequest(applicationUuid: string, conditionDocumentUuids: string[]): Promise<Response> {
    return fetchPost(`/applications/${applicationUuid}/condition-documents-solicitor-access`, { conditionDocumentUuids });
}

export async function applicationConditionDocumentRejectRequest(conditionDocumentUuid: string): Promise<Response> {
    return fetchPost(`/application-condition-documents/${conditionDocumentUuid}/reject`);
}

export async function applicationConditionDocumentResetApprovalStatusRequest(conditionDocumentUuid: string): Promise<Response> {
    return fetchPost(`/application-condition-documents/${conditionDocumentUuid}/reset-approval-status`);
}

export async function applicationConditionDocumentsListRequest(applicationUuid: string): Promise<Response> {
    return fetchGet(`/applications/${applicationUuid}/condition-documents`);
}

export async function applicationConditionSuggestionsListRequest(applicationUuid: string): Promise<Response> {
    return fetchGet(`/applications/${applicationUuid}/condition-suggestions`);
}

export async function applicationBorrowerConditionSuggestionsListRequest(applicationBorrowerUuid: string): Promise<Response> {
    return fetchGet(`/application-borrowers/${applicationBorrowerUuid}/condition-suggestions`);
}

export async function applicationPropertyConditionSuggestionsListRequest(applicationPropertyUuid: string): Promise<Response> {
    return fetchGet(`/application-properties/${applicationPropertyUuid}/condition-suggestions`);
}

export async function applicationConditionalsAddRequest(applicationUuid: string, conditions: string[]): Promise<Response> {
    return fetchPost(`/applications/${applicationUuid}/conditionals`, {
        conditions,
    });
}

export async function applicationConditionalsListRequest(applicationUuid: string): Promise<Response> {
    return fetchGet(`/applications/${applicationUuid}/conditionals`);
}

export async function applicationConditionalApproveRequest(conditionalUuid: string): Promise<Response> {
    return fetchPost(`/application-conditionals/${conditionalUuid}/approve`);
}

export async function applicationConditionalDeleteRequest(conditionalUuid: string): Promise<Response> {
    return fetchDelete(`/application-conditionals/${conditionalUuid}/delete`);
}

export async function applicationConditionalGetRequest(conditionalUuid: string): Promise<Response> {
    return fetchGet(`/application-conditionals/${conditionalUuid}`);
}

export async function applicationConditionalSendRequest(conditionalUuid: string, email?: string, firstName?: string, lastName?: string): Promise<Response> {
    return fetchPost(`/application-conditionals/${conditionalUuid}/send`, !!email ? {
        email,
        firstName,
        lastName,
    } : null);
}

export async function applicationConditionalCancelSigningRequest(conditionalUuid: string): Promise<Response> {
    return fetchPost(`/application-conditionals/${conditionalUuid}/cancel-signing`);
}

export async function applicationConditionalCheckSigningStatusRequest(conditionalUuid: string): Promise<Response> {
    return fetchPost(`/application-conditionals/${conditionalUuid}/check-signing-status`);
}

export async function applicationConditionalSendForSigningRequest(conditionalUuid: string): Promise<Response> {
    return fetchPost(`/application-conditionals/${conditionalUuid}/send-for-signing`);
}

export async function applicationConditionalConditionAddRequest(conditionalUuid: string, condition: IConditionalCondition): Promise<Response> {
    return fetchPost(`/application-conditionals/${conditionalUuid}/condition`, transformApplicationConditionalCondition(condition));
}

export async function applicationConditionalConditionDeleteRequest(conditionUuid: string): Promise<Response> {
    return fetchDelete(`/application-conditional-conditions/${conditionUuid}/delete`);
}

export async function applicationConditionalsStandardConditionsListRequest(): Promise<Response> {
    return fetchGet('/application-conditionals/standard-conditions');
}

export async function applicationConditionalApprovalRequest(applicationUuid: string): Promise<Response> {
    return fetchPost(`/applications/${applicationUuid}/conditional-approval`);
}

export async function applicationDisbursementsAddRequest(applicationUuid: string, disbursement: IApplicationDisbursement): Promise<Response> {
    return fetchPost(`/applications/${applicationUuid}/disbursements`, transformApplicationDisbursement(disbursement));
}

export async function applicationDisbursementsListRequest(applicationUuid: string): Promise<Response> {
    return fetchGet(`/applications/${applicationUuid}/disbursements`);
}

export async function applicationDisbursementDeleteRequest(applicationDisbursementUuid: string): Promise<Response> {
    return fetchDelete(`/application-disbursements/${applicationDisbursementUuid}`);
}

export async function applicationDisbursementUpdateRequest(applicationDisbursement: IApplicationDisbursement): Promise<Response> {
    return fetchPut(`/application-disbursements/${applicationDisbursement.uuid}`, transformApplicationDisbursement(applicationDisbursement));
}

export async function applicationDisbursementSuggestionsListRequest(): Promise<Response> {
    return fetchGet('/application-disbursements/suggestions');
}

export async function applicationFeesAddRequest(applicationUuid: string, fee: IApplicationFee): Promise<Response> {
    return fetchPost(`/applications/${applicationUuid}/fees`, transformApplicationFee(fee));
}

export async function applicationFeesListRequest(applicationUuid: string): Promise<Response> {
    return fetchGet(`/applications/${applicationUuid}/fees`);
}

export async function applicationFeeDeleteRequest(applicationFeeUuid: string): Promise<Response> {
    return fetchDelete(`/application-fees/${applicationFeeUuid}`);
}

export async function applicationFeeUpdateRequest(fee: IApplicationFee): Promise<Response> {
    return fetchPut(`/application-fees/${fee.uuid}`, transformApplicationFee(fee));
}

export async function applicationFormalApprovalConditionsRequest(applicationUuid: string, formalApprovalConditions: string): Promise<Response> {
    return fetchPost(`/applications/${applicationUuid}/formal-approval-conditions`, {
        formalApprovalConditions,
    });
}

export async function applicationFormalApprovalTermsRequest(applicationUuid: string, formalApprovalTerms: string): Promise<Response> {
    return fetchPost(`/applications/${applicationUuid}/formal-approval-terms`, {
        formalApprovalTerms,
    });
}

export async function applicationFormalsListRequest(applicationUuid: string): Promise<Response> {
    return fetchGet(`/applications/${applicationUuid}/formals`);
}

export async function applicationFormalAddRequest(applicationUuid: string): Promise<Response> {
    return fetchPost(`/applications/${applicationUuid}/formals`);
}

export async function applicationFormalApprovalRequest(applicationUuid: string): Promise<Response> {
    return fetchPost(`/applications/${applicationUuid}/formal-approval`);
}

export async function applicationFormalApprovalSendToRequest(formalUuid: string, email: string, firstName: string, lastName: string): Promise<Response> {
    return fetchPost(`/application-formals/${formalUuid}/send-to`, {
        email,
        firstName,
        lastName,
    });
}

export async function applicationFormalApprovalSendToBorrowerRequest(formalUuid: string): Promise<Response> {
    return fetchPost(`/application-formals/${formalUuid}/send-to-borrower`);
}

export async function applicationFormalApprovalSendToBrokerRequest(formalUuid: string): Promise<Response> {
    return fetchPost(`/application-formals/${formalUuid}/send-to-broker`);
}

export async function applicationHistoriesListRequest(applicationUuid: string): Promise<Response> {
    return fetchGet(`/applications/${applicationUuid}/history`);
}

export async function applicationInternalCommentsRequest(applicationUuid: string, internalComments: string): Promise<Response> {
    return fetchPost(`/applications/${applicationUuid}/internal-comments`, {
        internalComments,
    });
}

export async function applicationNotesAddRequest(applicationUuid: string, note: INote): Promise<Response> {
    return fetchPost(`/applications/${applicationUuid}/notes`, transformApplicationNote(note));
}

export async function applicationNotesListRequest(applicationUuid: string): Promise<Response> {
    return fetchGet(`/applications/${applicationUuid}/notes`);
}

export async function applicationGetRequest(applicationUuid: string): Promise<Response> {
    return fetchGet(`/applications/${applicationUuid}`);
}

export async function applicationLegalDocumentsRequest(applicationUuid: string): Promise<Response> {
    return fetchPost(`/applications/${applicationUuid}/legal-documents`);
}

export async function applicationGenerateMemoRequest(applicationUuid: string): Promise<Response> {
    return fetchPost(`/applications/${applicationUuid}/generate-memo`);
}

export async function applicationNewRequest(applicationUuid: string): Promise<Response> {
    return fetchPost(`/applications/${applicationUuid}/new`);
}

export async function applicationsOutstandingBrokerCommissionsListRequest(): Promise<Response> {
    return fetchGet('/applications/outstanding-broker-commissions');
}

export async function applicationMarkBrokerCommissionPaidRequest(applicationUuid: string): Promise<Response> {
    return fetchPost(`/applications/${applicationUuid}/mark-broker-commission-paid`);
}

export async function applicationReopenRequest(applicationUuid: string): Promise<Response> {
    return fetchPost(`/applications/${applicationUuid}/reopen`);
}

export async function applicationPropertiesListRequest(applicationUuid: string): Promise<Response> {
    return fetchGet(`/application/${applicationUuid}/properties`);
}

export async function applicationPropertiesValuationsOutstandingListRequest(): Promise<Response> {
    return fetchGet('/application-properties/valuations-outstanding');
}

export async function applicationPropertyGetRequest(applicationPropertyUuid: string): Promise<Response> {
    return fetchGet(`/application-properties/${applicationPropertyUuid}`);
}

export async function applicationPropertyTitleSearchAddRequest(
    applicationPropertyUuid: string,
    postcode: string,
    state: PropertyStateEnum,
    streetName: string,
    streetNumber: string,
    streetType: PropertyStreetTypeEnum,
    suburb: string,
    unitNumber: string,
): Promise<Response> {
    return fetchPost(`/application-properties/${applicationPropertyUuid}/title-search`, {
        postcode,
        state,
        streetName,
        streetNumber,
        streetType,
        suburb,
        unitNumber,
    });
}

export async function applicationPropertyTitleSearchCheckRequest(applicationPropertyUuid: string): Promise<Response> {
    return fetchPost(`/application-properties/${applicationPropertyUuid}/title-search-check`);
}

export async function applicationPropertyTitleSearchGetRequest(applicationPropertyUuid: string): Promise<Response> {
    return fetchGet(`/application-properties/${applicationPropertyUuid}/title-search`);
}

export async function applicationPropertyTitleSearchAddressGetRequest(applicationPropertyUuid: string): Promise<Response> {
    return fetchGet(`/application-properties/${applicationPropertyUuid}/title-search-address`);
}

export async function applicationPropertyTitleSearchOrderRequest(applicationPropertyUuid: string, titleSearchResultUuid: string): Promise<Response> {
    return fetchPost(`/application-properties/${applicationPropertyUuid}/title-search-order`, {
        titleSearchResultUuid,
    });
}

export async function applicationPropertyValuationDueDateRequest(applicationPropertyUuid: string, valuationDueDate: string): Promise<Response> {
    return fetchPost(`/application-properties/${applicationPropertyUuid}/valuation-due-date`, {
        valuationDueDate,
    });
}

export async function applicationPropertyValuationInspectionDateRequest(applicationPropertyUuid: string, valuationInspectionDate: string): Promise<Response> {
    return fetchPost(`/application-properties/${applicationPropertyUuid}/valuation-inspection-date`, {
        valuationInspectionDate,
    });
}

export async function applicationPropertyValuationReceivedDateRequest(applicationPropertyUuid: string, valuationReceivedDate: string): Promise<Response> {
    return fetchPost(`/application-properties/${applicationPropertyUuid}/valuation-received-date`, {
        valuationReceivedDate,
    });
}

export async function applicationPropertyValuationValueRequest(
    applicationPropertyUuid: string,
    valuationType: PropertyValuationTypeEnum | null,
    valuationValue: number,
): Promise<Response> {
    return fetchPost(`/application-properties/${applicationPropertyUuid}/valuation-value`, {
        valuationType,
        valuationValue,
    });
}

export async function applicationPropertyValuersAddRequest(applicationPropertyUuid: string, valuerUuid: string): Promise<Response> {
    return fetchPost(`/application-properties/${applicationPropertyUuid}/valuers`, {
        valuerUuid,
    });
}

export async function applicationPropertyValuersListRequest(applicationPropertyUuid: string): Promise<Response> {
    return fetchGet(`/application-properties/${applicationPropertyUuid}/valuers`);
}

export async function applicationPropertyValuersUnpaidRequest(): Promise<Response> {
    return fetchGet('/application-property-valuers/unpaid');
}

export async function applicationPropertyValuerMarkPaidRequest(applicationPropertyValuerUuid: string): Promise<Response> {
    return fetchPost(`/application-property-valuers/${applicationPropertyValuerUuid}/mark-paid`);
}

export async function applicationPropertyValuerQuoteReceivedRequest(
    applicationPropertyValuerUuid: string,
    quoteAmount: number,
    quoteReceivedDate: string,
    quoteTimeFrameDays: number,
): Promise<Response> {
    return fetchPost(`/application-property-valuers/${applicationPropertyValuerUuid}/quote-received`, {
        quoteAmount,
        quoteReceivedDate,
        quoteTimeFrameDays,
    });
}

export async function applicationPropertyValuerQuoteRequestedRequest(
    applicationPropertyValuerUuid: string,
    quoteRequestedDate: string,
    valuationType: PropertyValuerValuationTypeEnum | null,
): Promise<Response> {
    return fetchPost(`/application-property-valuers/${applicationPropertyValuerUuid}/quote-requested`, {
        quoteRequestedDate,
        valuationType,
    });
}

export async function applicationPropertyValuerValuationOrderedRequest(
    applicationPropertyValuerUuid: string,
    dueDate: string,
    valuationOrderedDateTime: string,
): Promise<Response> {
    return fetchPost(`/application-property-valuers/${applicationPropertyValuerUuid}/valuation-ordered`, {
        dueDate,
        valuationOrderedDateTime,
    });
}

export async function applicationPropertyValuerValuationReceivedRequest(
    applicationPropertyValuerUuid: string,
    valuationInspectionDate: string,
    valuationReceivedDateTime: string,
    valuationValue: number,
): Promise<Response> {
    return fetchPost(`/application-property-valuers/${applicationPropertyValuerUuid}/valuation-received`, {
        valuationInspectionDate,
        valuationReceivedDateTime,
        valuationValue,
    });
}

export async function applicationPropertyValuerQuoteAmountRequest(applicationPropertyValuerUuid: string, quoteAmount: number): Promise<Response> {
    return fetchPost(`/application-property-valuers/${applicationPropertyValuerUuid}/quote-amount`, {
        quoteAmount,
    });
}

export async function applicationPropertyValuerQuoteTimeFrameRequest(applicationPropertyValuerUuid: string, quoteTimeFrame: number): Promise<Response> {
    return fetchPost(`/application-property-valuers/${applicationPropertyValuerUuid}/quote-time-frame`, {
        quoteTimeFrame,
    });
}

export async function applicationRecalculateRequest(
    applicationUuid: string,
    applicationFee: number,
    applicationFeeFormat: ApplicationFeeFormatEnum,
    applicationFeePercentage: number,
    brokerageFee: number,
    brokerageFeeFormat: BrokerageFeeFormatEnum,
    brokerageFeePercentage: number,
    commitmentFee: number,
    establishmentFee: number,
    establishmentFeeFormat: EstablishmentFeeFormatEnum,
    establishmentFeePercentage: number,
    estimatedOutlays: number,
    interestRate: number,
    legalFees: number,
    loanAmount: number,
    lvr: number,
    maximumLvr: number,
    netBalance: number,
    termMonths: number,
): Promise<Response> {
    return fetchPost(`/applications/${applicationUuid}/recalculate`, {
        applicationFee,
        applicationFeeFormat,
        applicationFeePercentage,
        brokerageFee,
        brokerageFeeFormat,
        brokerageFeePercentage,
        commitmentFee,
        establishmentFee,
        establishmentFeeFormat,
        establishmentFeePercentage,
        estimatedOutlays,
        interestRate,
        legalFees,
        loanAmount,
        lvr,
        maximumLvr,
        netBalance,
        termMonths,
    });
}

export async function applicationRfiItemDeleteRequest(applicationRfiItemUuid: string): Promise<Response> {
    return fetchDelete(`/application-rfi-items/${applicationRfiItemUuid}`);
}

export async function applicationRfiItemRequestedRequest(applicationRfiItemUuid: string): Promise<Response> {
    return fetchPost(`/application-rfi-items/${applicationRfiItemUuid}/requested`);
}

export async function applicationRfiItemSuppliedRequest(applicationRfiItemUuid: string): Promise<Response> {
    return fetchPost(`/application-rfi-items/${applicationRfiItemUuid}/supplied`);
}

export async function applicationRfiItemsAddRequest(
    applicationUuid: string,
    applicationBorrowerUuid: string,
    applicationConditionUuid: string,
    applicationPropertyUuid: string,
    conditionType: ConditionTypeEnum,
): Promise<Response> {
    return fetchPost(`/applications/${applicationUuid}/rfi-items`, {
        applicationBorrowerUuid,
        applicationConditionUuid,
        applicationPropertyUuid,
        conditionType,
    });
}

export async function applicationRfiItemsListRequest(applicationUuid: string): Promise<Response> {
    return fetchGet(`/applications/${applicationUuid}/rfi-items`);
}

export async function applicationSettlementRequest(applicationUuid: string): Promise<Response> {
    return fetchPost(`/applications/${applicationUuid}/settlement`);
}

export async function applicationSettlementDateRequest(applicationUuid: string, settlementDate: string): Promise<Response> {
    return fetchPost(`/applications/${applicationUuid}/settlement-date`, {
        settlementDate,
    });
}

export async function applicationSolicitorInstructionsSentTimeRequest(applicationUuid: string, solicitorInstructionsSentTime: string): Promise<Response> {
    return fetchPost(`/applications/${applicationUuid}/solicitor-instructions-sent-time`, {
        solicitorInstructionsSentTime,
    });
}

export async function applicationUnderwritingRequest(applicationUuid: string): Promise<Response> {
    return fetchPost(`/applications/${applicationUuid}/underwriting`);
}

export async function applicationUpdateRequest(application: IApplication): Promise<Response> {
    return fetchPut(`/applications/${application.uuid}`, transformApplication(application));
}

export async function applicationWarehousedRequest(applicationUuid: string, settlementDate: string, sendEmail: boolean, force: boolean = false): Promise<Response> {
    return fetchPost(`/applications/${applicationUuid}/warehoused`, {
        sendEmail,
        settlementDate,
    }, { force });
}

export async function applicationWarehousesAddRequest(applicationUuid: string, warehouse: IApplicationWarehouse): Promise<Response> {
    return fetchPost(`/applications/${applicationUuid}/warehouses`, transformApplicationWarehouse(warehouse));
}

export async function applicationWarehousesListRequest(applicationUuid: string): Promise<Response> {
    return fetchGet(`/applications/${applicationUuid}/warehouses`);
}

export async function applicationWarehouseDeleteRequest(applicationWarehouseUuid: string): Promise<Response> {
    return fetchDelete(`/application-warehouses/${applicationWarehouseUuid}`);
}

export async function applicationWarehouseUpdateRequest(applicationWarehouse: IApplicationWarehouse): Promise<Response> {
    return fetchPost(`/application-warehouses/${applicationWarehouse.uuid}`, transformApplicationWarehouse(applicationWarehouse));
}